/**
 * groupCameraCounts — how many cameras each group holds INCLUDING its sub-groups.
 *
 * A group's own `camera_count` is its direct members only, so a parent such as "Pune" whose
 * cameras are all filed under "Pune › Gate" and "Pune › Lobby" read 0 — the tree said the
 * site was empty. This counts down the whole subtree, and counts each camera ONCE: a camera
 * filed in both a parent and one of its children is still one camera.
 *
 * Built from the camera list's own `groups` arrays (every camera the node lists carries the
 * groups it is filed in), so it needs no extra request. Cameras the user cannot see are not in
 * that list and so are not counted — the number matches what the operator can actually open.
 */
type GroupNode = { id: string; parent_id?: string | null; children?: GroupNode[] };
type CameraWithGroups = { groups?: { id: string }[] | null };

export interface GroupCameraCount {
  /** Distinct cameras in the group or any group below it. */
  total: number;
  /** Cameras filed directly in the group. */
  direct: number;
}

export function groupCameraCounts(tree: GroupNode[], cameras: CameraWithGroups[]): Map<string, GroupCameraCount> {
  // Each group's ancestry, root first, walked from the tree (the list may be nested or flat).
  const parentOf = new Map<string, string | null>();
  const walk = (nodes: GroupNode[], parent: string | null) =>
    nodes.forEach((g) => {
      parentOf.set(g.id, parent ?? g.parent_id ?? null);
      if (g.children?.length) walk(g.children, g.id);
    });
  walk(tree, null);

  const counts = new Map<string, GroupCameraCount>();
  for (const id of parentOf.keys()) counts.set(id, { total: 0, direct: 0 });

  for (const cam of cameras) {
    const own = (cam.groups || []).map((g) => g.id).filter((id) => parentOf.has(id));
    // Every group this camera counts toward: the ones it is filed in and all their ancestors —
    // in a Set, so a camera in both a parent and its child adds one to the parent, not two.
    const reach = new Set<string>();
    for (const id of own) {
      counts.get(id)!.direct += 1;
      for (let g: string | null = id; g && !reach.has(g); g = parentOf.get(g) ?? null) reach.add(g);
    }
    reach.forEach((id) => (counts.get(id)!.total += 1));
  }
  return counts;
}
